import axios from 'axios';
import {getAwpClientHeaders} from '../../../../../session/awpRequestHeaders';
import {isLocalEntityRef} from '../../../../../utils/entityLinks';

export class SourceUploadDuplicateError extends Error {
    constructor(referenceId, message = 'This file is already in My files.', fileName = null) {
        super(message);
        this.name = 'SourceUploadDuplicateError';
        this.referenceId = referenceId;
        this.fileName = fileName;
    }
}

export class SourceDeletionChoiceRequiredError extends Error {
    constructor(details, message = 'Choose whether to remove this placement only or delete the file everywhere.') {
        super(message);
        this.name = 'SourceDeletionChoiceRequiredError';
        this.details = details;
    }
}

export class PmDocumentNotReadyError extends Error {
    constructor(message = 'PM document not ready', status = 404) {
        super(message);
        this.name = 'PmDocumentNotReadyError';
        this.status = status;
    }
}

export function getSourcesApiBase() {
    return (process.env.REACT_APP_SOURCES_API_BASE || process.env.REACT_APP_SOURCES_WS_BASE || 'https://awp.minsky.ai').replace(/\/$/, '');
}

// Builds the {level, distribution, compartment} payload sent as `classification` on
// web-import requests. Returns null when nothing was selected, so callers can omit the
// field entirely rather than sending an empty object.
function buildClassificationPayload(classificationLevel, distribution, compartment) {
    const hasDistribution = Array.isArray(distribution) && distribution.length > 0;
    const hasCompartment = Array.isArray(compartment) && compartment.length > 0;
    if (!classificationLevel && !hasDistribution && !hasCompartment) {
        return null;
    }
    const payload = {};
    if (classificationLevel) payload.level = classificationLevel;
    if (hasDistribution) payload.distribution = distribution;
    if (hasCompartment) payload.compartment = compartment;
    return payload;
}

function getSourcesUploadUrl() {
    return (process.env.REACT_APP_SOURCES_UPLOAD_URL || `${getSourcesApiBase()}/api/data-items/upload/file`).replace(/\/$/, '');
}

function getSourcesUploadFieldName() {
    return process.env.REACT_APP_SOURCES_UPLOAD_FIELD_NAME || 'file';
}

function getFirstDefined(...values) {
    return values.find((value) => value !== undefined && value !== null && value !== '');
}

function buildSourcesQuery(userId) {
    return new URLSearchParams({
        userId,
        page: '0',
        size: '500',
    });
}

function withFolderIdQuery(userId, folderId) {
    const query = buildSourcesQuery(userId);
    query.set('folderId', String(folderId));
    return query.toString();
}

function getBranchForView(view) {
    if (!view || view.type === 'root') {
        return null;
    }

    if (view.type === 'preset') {
        return view.id || null;
    }

    if (view.type === 'folder') {
        return view.branch || null;
    }

    return null;
}

function getFolderIdForView(view) {
    if (view?.type !== 'folder') {
        return null;
    }

    const pathIds = Array.isArray(view.pathIds) ? view.pathIds : [];
    return pathIds[pathIds.length - 1] || view.id || null;
}

export function getParentFolderIdForView(view) {
    if (view?.type !== 'folder') {
        return null;
    }

    return getFolderIdForView(view);
}

export function buildSourcesListUrl(view, userId) {
    if (!userId) {
        throw new Error('User ID is required to load source items.');
    }

    const baseUrl = getSourcesApiBase();
    const query = buildSourcesQuery(userId).toString();
    const branch = getBranchForView(view);
    const folderId = getFolderIdForView(view);

    // Branch-less folder views (e.g. folders created from root) should still load that folder's children.
    if (view?.type === 'folder' && folderId && !branch) {
        return `${baseUrl}/api/data-items?${withFolderIdQuery(userId, folderId)}`;
    }

    if (!branch) {
        return `${baseUrl}/api/data-items?${query}`;
    }

    // Backend has no /api/data-items/all — at the "All data" preset root, list the global library (no userId).
    if (view.type === 'preset' && branch === 'all') {
        const globalQuery = new URLSearchParams({page: '0', size: '500'}).toString();
        return `${baseUrl}/api/data-items?${globalQuery}`;
    }

    // Nested folder under the "All data" branch: use legacy query form (user-scoped folder children).
    if (view.type === 'folder' && branch === 'all' && folderId) {
        return `${baseUrl}/api/data-items?${withFolderIdQuery(userId, folderId)}`;
    }

    let path = `${baseUrl}/api/data-items/${branch}`;
    if (folderId) {
        // Collected Materials uses virtual CT folders keyed by collectionTaskId (not data_item ids).
        if (branch === 'collected-materials' || branch === 'collected') {
            path += `/tasks/${encodeURIComponent(folderId)}`;
        } else {
            path += `/${encodeURIComponent(folderId)}`;
        }
    }

    return `${path}?${query}`;
}

export async function fetchSourcesList({view, userId, signal}) {
    const url = buildSourcesListUrl(view, userId);
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to load source items (${response.status})`));
    }

    return await response.json();
}

function normalizeListPayload(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    const record = payload || {};
    const listCandidate = [record.items, record.content, record.data, record.results].find(Array.isArray);
    return Array.isArray(listCandidate) ? listCandidate : [];
}

function isFolderLike(item) {
    return Boolean(
        item?.folderId
        || item?.folderName
        || item?.type === 'folder'
        || item?.type === 'FOLDER'
        || item?.itemType === 'folder'
        || item?.itemType === 'FOLDER'
        || item?.entityType === 'folder'
        || item?.entityType === 'FOLDER'
    );
}

export async function fetchSourcesFoldersByParent({userId, parentFolderId = null, signal}) {
    if (!userId) {
        throw new Error('User ID is required to load folders.');
    }

    const baseUrl = getSourcesApiBase();
    const query = buildSourcesQuery(userId);
    if (parentFolderId) {
        query.set('folderId', String(parentFolderId));
    }

    const response = await fetch(`${baseUrl}/api/data-items?${query.toString()}`, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to load folders (${response.status})`));
    }

    const payload = await response.json();
    const items = normalizeListPayload(payload);

    return items
        .filter((item) => isFolderLike(item))
        .map((item) => ({
            id: String(getFirstDefined(item?.id, item?.folderId, item?.uuid, item?.localId) || ''),
            name: String(getFirstDefined(item?.name, item?.folderName, item?.title, '') || ''),
        }))
        .filter((folder) => folder.id && folder.name);
}

function extractErrorMessage(errorText, fallback) {
    if (!errorText) return fallback;
    try {
        const parsed = JSON.parse(errorText);
        if (parsed?.deletionChoiceRequired) {
            return 'This file exists in multiple locations. Choose how to delete it.';
        }
        return parsed?.message || parsed?.error || parsed?.detail || fallback;
    } catch (_) {
        return errorText || fallback;
    }
}

function parseDeletionConflict(errorText) {
    if (!errorText) {
        return null;
    }

    try {
        const parsed = JSON.parse(errorText);
        return parsed?.deletionChoiceRequired ? parsed : null;
    } catch (_) {
        return null;
    }
}

export async function fetchSourceFile({fileId, userId, signal}) {
    if (!fileId) {
        throw new Error('File ID is required to load a source file.');
    }

    if (!userId) {
        throw new Error('User ID is required to load a source file.');
    }

    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    const response = await fetch(`${baseUrl}/api/data-items/${encodeURIComponent(fileId)}?${query}`, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to load source file (${response.status})`));
    }

    return await response.json();
}

function normalizeCreatedFolder(payload, fallbackName) {
    const folder = payload?.item || payload?.folder || payload?.data || payload || {};

    return {
        id: getFirstDefined(folder?.id, folder?.folderId, folder?.uuid, folder?.localId),
        name: getFirstDefined(folder?.name, folder?.folderName, folder?.title, fallbackName),
    };
}

export async function createSourcesFolderInParent({userId, name, parentFolderId = null, signal}) {
    if (!userId) {
        throw new Error('User ID is required to create a folder.');
    }

    const trimmedName = String(name || '').trim();
    if (!trimmedName) {
        throw new Error('Folder name is required.');
    }

    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    const body = {
        type: 'folder',
        name: trimmedName,
    };

    if (parentFolderId !== null && parentFolderId !== undefined && parentFolderId !== '') {
        body.parentFolderId = parentFolderId;
    }

    const response = await fetch(`${baseUrl}/api/data-items?${query}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-User-Id': userId,
        },
        body: JSON.stringify(body),
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to create folder (${response.status})`));
    }

    let payload = null;
    try {
        payload = await response.json();
    } catch (_) {
        payload = null;
    }

    const createdFolder = normalizeCreatedFolder(payload, trimmedName);
    if (!createdFolder.id) {
        throw new Error('Folder was created, but no folder id was returned.');
    }

    return createdFolder;
}

export async function createSourcesFolder({view, userId, name, signal}) {
    return createSourcesFolderInParent({
        userId,
        name,
        parentFolderId: getParentFolderIdForView(view),
        signal,
    });
}

export async function renameSourcesFolder({folderId, userId, name, signal}) {
    if (!folderId) {
        throw new Error('Folder ID is required to rename a folder.');
    }
    if (!userId) {
        throw new Error('User ID is required to rename a folder.');
    }

    const trimmedName = String(name || '').trim();
    if (!trimmedName) {
        throw new Error('Folder name is required.');
    }

    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    const response = await fetch(`${baseUrl}/api/data-items/${encodeURIComponent(folderId)}?${query}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-User-Id': userId,
        },
        body: JSON.stringify({name: trimmedName}),
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to rename folder (${response.status})`));
    }

    let payload = null;
    try {
        payload = await response.json();
    } catch (_) {
        payload = null;
    }

    return normalizeCreatedFolder(payload, trimmedName);
}

async function updateSourcesItem({
    itemId,
    userId,
    payload,
    signal,
    missingIdErrorMessage,
    missingUserIdErrorMessage,
    failedErrorLabel,
}) {
    if (!itemId) {
        throw new Error(missingIdErrorMessage);
    }
    if (!userId) {
        throw new Error(missingUserIdErrorMessage);
    }

    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    const response = await fetch(`${baseUrl}/api/data-items/${encodeURIComponent(itemId)}?${query}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-User-Id': userId,
        },
        body: JSON.stringify(payload || {}),
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to update ${failedErrorLabel} (${response.status})`));
    }

    return true;
}

export async function moveSourcesFolder({folderId, userId, parentFolderId = null, signal}) {
    return updateSourcesItem({
        itemId: folderId,
        userId,
        payload: parentFolderId == null ? {parentFolderId: null, moveToRoot: true} : {parentFolderId},
        signal,
        missingIdErrorMessage: 'Folder ID is required to move a folder.',
        missingUserIdErrorMessage: 'User ID is required to move a folder.',
        failedErrorLabel: 'folder',
    });
}

export async function moveSourcesFile({fileId, userId, parentFolderId = null, signal}) {
    return updateSourcesItem({
        itemId: fileId,
        userId,
        payload: parentFolderId == null ? {parentFolderId: null, moveToRoot: true} : {parentFolderId},
        signal,
        missingIdErrorMessage: 'File ID is required to move a file.',
        missingUserIdErrorMessage: 'User ID is required to move a file.',
        failedErrorLabel: 'file',
    });
}

// Updates the Classification Level/Distribution/Compartment on an already-added source. Targets
// the specific row being viewed (canonical FILE or REFERENCE) — matches the existing
// rename/move behavior of not propagating to other rows of the same underlying file.
export async function updateSourceClassification({fileId, userId, classificationLevel = null, distribution = null, compartment = null, signal}) {
    const classification = buildClassificationPayload(classificationLevel, distribution, compartment) || {level: 'U'};
    return updateSourcesItem({
        itemId: fileId,
        userId,
        payload: {classification},
        signal,
        missingIdErrorMessage: 'File ID is required to update classification.',
        missingUserIdErrorMessage: 'User ID is required to update classification.',
        failedErrorLabel: 'classification',
    });
}

async function removeSourcesItem({
    itemId,
    userId,
    signal,
    purgeReferencedCanonical,
    missingIdErrorMessage,
    missingUserIdErrorMessage,
    failedErrorLabel,
}) {
    if (!itemId) {
        throw new Error(missingIdErrorMessage);
    }
    if (!userId) {
        throw new Error(missingUserIdErrorMessage);
    }

    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId});
    if (purgeReferencedCanonical !== undefined && purgeReferencedCanonical !== null) {
        query.set('purgeReferencedCanonical', String(Boolean(purgeReferencedCanonical)));
    }
    const response = await fetch(`${baseUrl}/api/data-items/${encodeURIComponent(itemId)}?${query}`, {
        method: 'DELETE',
        headers: {
            Accept: 'application/json',
            'X-User-Id': userId,
        },
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        const deletionConflict = response.status === 409 ? parseDeletionConflict(errorText) : null;
        if (deletionConflict) {
            throw new SourceDeletionChoiceRequiredError(deletionConflict);
        }
        throw new Error(extractErrorMessage(errorText, `Failed to remove ${failedErrorLabel} (${response.status})`));
    }

    return true;
}

export async function removeSourcesFolder({folderId, userId, signal, purgeReferencedCanonical}) {
    return removeSourcesItem({
        itemId: folderId,
        userId,
        signal,
        purgeReferencedCanonical,
        missingIdErrorMessage: 'Folder ID is required to remove a folder.',
        missingUserIdErrorMessage: 'User ID is required to remove a folder.',
        failedErrorLabel: 'folder',
    });
}

export async function removeSourcesFile({fileId, userId, signal, purgeReferencedCanonical}) {
    return removeSourcesItem({
        itemId: fileId,
        userId,
        signal,
        purgeReferencedCanonical,
        missingIdErrorMessage: 'File ID is required to remove a file.',
        missingUserIdErrorMessage: 'User ID is required to remove a file.',
        failedErrorLabel: 'file',
    });
}

function normalizeUploadedFile(payload, fallback) {
    const record = payload || {};
    const item = record.dataItem || record.item || record.file || record.data || record;
    const referenceId = getFirstDefined(record.referenceId, item?.id);

    return {
        id: String(getFirstDefined(referenceId, item?.id, item?.fileId, item?.uuid, item?.localId) || ''),
        referenceId: referenceId != null ? String(referenceId) : undefined,
        // canonicalItemId is the canonical FILE id (referencedDataItemId on the REFERENCE row).
        // The upload queue endpoint returns canonical FILE rows keyed by this id, so callers
        // that need to match against the queue must use canonicalItemId, not referenceId.
        canonicalItemId: item?.referencedDataItemId != null ? String(item.referencedDataItemId) : undefined,
        name: String(getFirstDefined(item?.name, item?.fileName, fallback.name) || fallback.name),
        type: 'file',
        mimeType: getFirstDefined(item?.mimeType, item?.fileType, fallback.mimeType) || null,
        size: Number(getFirstDefined(item?.size, item?.fileSize, fallback.size) || 0) || null,
        parentFolderId: String(getFirstDefined(item?.parentFolderId, item?.folderId, fallback.parentFolderId) || ''),
        ownerId: String(getFirstDefined(item?.ownerId, item?.userId, fallback.ownerId) || ''),
        status: getFirstDefined(record.status, item?.status) || null,
        stage: getFirstDefined(record.stage, item?.meta?.processing?.stage) || null,
        progressPercent: getFirstDefined(record.progressPercent, item?.meta?.processing?.progressPercent) ?? null,
        meta: item?.meta || null,
        classification: item?.classification || null,
        raw: item,
    };
}

export async function fetchSourcePm({referenceId, userId, role, signal}) {
    if (!referenceId) throw new Error('referenceId is required');
    if (!userId) throw new Error('User ID is required');
    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId});
    // Only send role when explicitly requested. Omitting it lets the backend serve
    // its English-first default (translated when a translation exists, else original).
    if (role != null && role !== '') {
        query.set('role', String(role));
    }
    const response = await fetch(
        `${baseUrl}/api/data-items/${encodeURIComponent(referenceId)}/pm?${query.toString()}`,
        {method: 'GET', headers: {Accept: 'application/json', 'X-User-Id': userId}, signal},
    );
    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        const message = extractErrorMessage(errorText, `Failed to get PM document (${response.status})`);
        if (response.status === 404 && /pm document not ready/i.test(message)) {
            throw new PmDocumentNotReadyError(message, response.status);
        }
        throw new Error(message);
    }
    return response.json();
}

/**
 * Fetches MinerU layout JSON for a data item.
 * Returns `{ contentList, modelOutput, pageCount, blockCount }`.
 * Resolves to `null` (no throw) when no layout data is stored yet (404).
 */
export async function fetchSourceLayout({referenceId, userId, signal}) {
    if (!referenceId) throw new Error('referenceId is required');
    if (!userId) throw new Error('User ID is required');
    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    const response = await fetch(
        `${baseUrl}/api/data-items/${encodeURIComponent(referenceId)}/layout?${query}`,
        {method: 'GET', headers: {Accept: 'application/json', 'X-User-Id': userId}, signal},
    );
    if (response.status === 404) return null;
    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to get layout data (${response.status})`));
    }
    return response.json();
}

/**
 * Returns the URL that streams the annotated layout PDF for a data item.
 * No separate fetch needed — use directly as an <iframe src> or pdfjs load target.
 */
export function getLayoutPdfUrl({referenceId, userId}) {
    if (!referenceId || !userId) return null;
    const baseUrl = getSourcesApiBase();
    return `${baseUrl}/api/data-items/${encodeURIComponent(referenceId)}/layout-pdf`
        + `?userId=${encodeURIComponent(userId)}`;
}

/**
 * Returns the URL for a rendered PNG of one PDF page (0-based index).
 * Used with model_output bbox overlays in the sources viewer.
 */
export function getPageImageUrl({referenceId, userId, pageIndex}) {
    if (!referenceId || !userId || pageIndex == null) return null;
    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    return `${baseUrl}/api/data-items/${encodeURIComponent(referenceId)}/page-image/${pageIndex}?${query}`;
}

/**
 * Returns a URL that streams the raw file through dash-api.
 * No separate fetch needed — the URL is constructed directly and used as
 * an <iframe src>, <audio src>, or <video src>.
 */
export function getRawStreamUrl({referenceId, userId, disposition = 'inline'}) {
    if (!referenceId || !userId) return null;
    const baseUrl = getSourcesApiBase();
    return `${baseUrl}/api/data-items/${encodeURIComponent(referenceId)}/raw/stream`
        + `?userId=${encodeURIComponent(userId)}&disposition=${disposition}`;
}

export async function fetchSourceRawUrl({referenceId, userId, signal}) {
    if (!referenceId) {
        throw new Error('referenceId is required');
    }
    if (!userId) {
        throw new Error('User ID is required');
    }
    return {url: getRawStreamUrl({referenceId, userId, disposition: 'inline'})};
}

export async function fetchSourceDownloadUrl({referenceId, userId}) {
    if (!referenceId) {
        throw new Error('referenceId is required');
    }
    if (!userId) {
        throw new Error('User ID is required');
    }
    return {url: getRawStreamUrl({referenceId, userId, disposition: 'attachment'})};
}

function toUploadError(error, fileName = null) {
    if (axios.isCancel(error) || error?.name === 'AbortError' || error?.name === 'CanceledError') {
        const cancelError = new Error('Upload canceled');
        cancelError.name = 'AbortError';
        return cancelError;
    }

    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 409 && data?.referenceId != null && !data?.deletionChoiceRequired) {
        const label = fileName ? `"${fileName}"` : 'This file';
        const message = data?.message && data.message !== 'File already uploaded'
            ? data.message
            : `${label} is already in My files.`;
        return new SourceUploadDuplicateError(data.referenceId, message, fileName);
    }

    const responseMessage = typeof data === 'string' ? data : data?.message;

    return new Error(
        responseMessage
        || error?.message
        || `Failed to upload file${status ? ` (${status})` : ''}. Verify REACT_APP_SOURCES_UPLOAD_URL and multipart field configuration.`,
    );
}

export async function uploadSourceFile({
    file,
    userId,
    parentFolderId = null,
    classificationLevel = null,
    distribution = null,
    compartment = null,
    signal,
    onProgress,
}) {
    if (!userId) {
        throw new Error('User ID is required to upload a file.');
    }

    if (!file) {
        throw new Error('A file is required to upload.');
    }

    const url = new URL(getSourcesUploadUrl());
    url.searchParams.set('userId', userId);

    const body = new FormData();
    body.append(getSourcesUploadFieldName(), file, file.name);
    body.append('userId', userId);
    body.append('name', file.name);

    if (file.type) {
        body.append('mimeType', file.type);
    }

    if (parentFolderId) {
        url.searchParams.set('parentFolderId', parentFolderId);
        body.append('parentFolderId', parentFolderId);
    }

    if (classificationLevel) {
        body.append('classificationLevel', classificationLevel);
    }

    if (Array.isArray(distribution)) {
        distribution.forEach((value) => body.append('distribution', value));
    }

    if (Array.isArray(compartment)) {
        compartment.forEach((value) => body.append('compartment', value));
    }

    try {
        const response = await axios.post(url.toString(), body, {
            signal,
            headers: {
                ...getAwpClientHeaders(),
                Accept: 'application/json',
                'X-User-Id': userId,
            },
            onUploadProgress: (progressEvent) => {
                if (typeof onProgress !== 'function') {
                    return;
                }

                const total = progressEvent.total || file.size || 0;
                const loaded = Math.min(progressEvent.loaded || 0, total || progressEvent.loaded || 0);
                const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;
                onProgress({
                    loaded,
                    total: total || null,
                    progress,
                });
            },
        });

        const uploadedFile = normalizeUploadedFile(response.data, {
            name: file.name,
            mimeType: file.type || null,
            size: file.size,
            parentFolderId,
            ownerId: userId,
        });

        if (!uploadedFile.id) {
            throw new Error('The upload succeeded, but the server did not return a file id.');
        }

        return uploadedFile;
    } catch (error) {
        throw toUploadError(error, file?.name || null);
    }
}

/**
 * Fetches the effective upload-rail feature config (e.g. whether web import is enabled).
 * Public endpoint — resolves the runtime feature-flag override with an application.yml fallback.
 * Returns `{ urlImportEnabled }`; defaults to `{ urlImportEnabled: false }` if the request fails.
 */
export async function fetchUploadConfig({signal} = {}) {
    const baseUrl = getSourcesApiBase();
    const response = await fetch(`${baseUrl}/api/data-items/upload/config`, {
        method: 'GET',
        headers: {Accept: 'application/json'},
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to load upload config (${response.status})`));
    }

    const payload = await response.json();
    return {urlImportEnabled: Boolean(payload?.urlImportEnabled)};
}

/**
 * Fetches canonical FILE rows whose pipeline is still in-flight (status=pending|processing)
 * for the given user. Used to hydrate the Upload Queue on page load so in-progress uploads
 * survive browser refreshes and appear in new browser sessions.
 *
 * Returns an array of normalized items (same shape as normalizeUploadedFile):
 *   { id, name, mimeType, size, status, stage, progressPercent, createdAt, raw }
 */
export async function fetchUploadQueue({userId, signal} = {}) {
    const baseUrl = getSourcesApiBase();
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    const response = await fetch(`${baseUrl}/api/data-items/upload/queue?${params}`, {
        method: 'GET',
        headers: {Accept: 'application/json'},
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to load upload queue (${response.status})`));
    }

    const payload = await response.json();
    const items = Array.isArray(payload?.items) ? payload.items : [];
    return items.map((item) => ({
        id: String(item?.id ?? ''),
        name: String(item?.name ?? 'Untitled'),
        mimeType: item?.mimeType || null,
        size: Number(item?.size || 0),
        status: item?.status || null,
        stage: item?.meta?.processing?.stage || null,
        progressPercent: item?.meta?.processing?.progressPercent ?? null,
        createdAt: item?.createdAt || null,
        raw: item,
    }));
}

/**
 * Imports a data item from a web URL. Fetches the page server-side, converts HTML → ProseMirror,
 * and creates a data item (canonical + reference). Returns a normalized uploaded-file record.
 */
export async function createSourceFromUrl({url, userId, parentFolderId = null, name = null, classificationLevel = null, distribution = null, compartment = null, signal}) {
    if (!userId) {
        throw new Error('User ID is required to import from a URL.');
    }
    const trimmedUrl = String(url || '').trim();
    if (!trimmedUrl) {
        throw new Error('A URL is required to import.');
    }

    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    const body = {url: trimmedUrl};
    if (name) body.name = name;
    if (parentFolderId) body.parentFolderId = parentFolderId;
    const classification = buildClassificationPayload(classificationLevel, distribution, compartment);
    if (classification) body.classification = classification;

    const response = await fetch(`${baseUrl}/api/data-items/from-url?${query}`, {
        method: 'POST',
        headers: {
            ...getAwpClientHeaders(),
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-User-Id': userId,
        },
        body: JSON.stringify(body),
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to import URL (${response.status})`));
    }

    const payload = await response.json();
    return normalizeUploadedFile(payload, {name: name || trimmedUrl, mimeType: 'html', size: 0, ownerId: userId, parentFolderId});
}

/**
 * Imports a data item from pasted content (plain text or HTML). Creates a data item
 * (canonical + reference). Returns a normalized uploaded-file record.
 */
export async function createSourceFromContent({htmlContent = null, plainText = null, name = null, userId, parentFolderId = null, classificationLevel = null, distribution = null, compartment = null, signal}) {
    if (!userId) {
        throw new Error('User ID is required to import pasted content.');
    }
    if (!String(htmlContent || '').trim() && !String(plainText || '').trim()) {
        throw new Error('Some content is required to import.');
    }

    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    const body = {};
    if (htmlContent && String(htmlContent).trim()) body.htmlContent = htmlContent;
    if (plainText && String(plainText).trim()) body.plainText = plainText;
    if (name) body.name = name;
    if (parentFolderId) body.parentFolderId = parentFolderId;
    const classification = buildClassificationPayload(classificationLevel, distribution, compartment);
    if (classification) body.classification = classification;

    const response = await fetch(`${baseUrl}/api/data-items/from-content?${query}`, {
        method: 'POST',
        headers: {
            ...getAwpClientHeaders(),
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-User-Id': userId,
        },
        body: JSON.stringify(body),
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to import content (${response.status})`));
    }

    const payload = await response.json();
    return normalizeUploadedFile(payload, {name: name || 'Pasted content', mimeType: null, size: 0, ownerId: userId, parentFolderId});
}

/**
 * Fetches lightweight segment stubs for a data item: [{id, numberInItem, pmBlockIndex, type}].
 * pmBlockIndex is the 0-based top-level PM block index (content_path[0]) — maps block clicks to
 * DB segment ids. numberInItem counts sentences on sentence-segmented docs, so do not treat it
 * as a block index (kept only as a fallback for segments without a PM anchor).
 */
export async function fetchDataItemSegments({referenceId, userId, signal} = {}) {
    if (!referenceId) throw new Error('referenceId is required');
    const baseUrl = getSourcesApiBase();
    const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    const response = await fetch(
        `${baseUrl}/api/data-items/${encodeURIComponent(referenceId)}/segments${query}`,
        {method: 'GET', headers: {...getAwpClientHeaders(), Accept: 'application/json'}, signal},
    );
    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to load segments (${response.status})`));
    }
    return response.json();
}

/**
 * Manually links a recognized-but-unlinked mention in the read-only Data Items viewer to a chosen
 * Wikidata or custom (D-) entity. Calls POST /api/data-items/{referenceId}/manual-link, which
 * patches the ORIGINAL (English) PM document's content_json and refreshes segment rollups.
 *
 * Offsets are paragraph-relative char offsets within the top-level PM block (pmBlockIndex). The
 * server validates that mentionText equals blockText[start:end] before applying the mark.
 *
 * Pass a Wikidata QID via {@code wikidataEntity.id} or a custom entity via {@code localEntityRef}
 * (also accepted when {@code wikidataEntity.id} is already a D-id ref).
 *
 * @returns {Promise<Object>} the resulting entity attrs: { id, label, entityType, description?, imageUrl?, linked, duplicate }
 */
export async function linkDataItemEntity({
    referenceId,
    userId,
    pmBlockIndex,
    paragraphStartOffset,
    paragraphEndOffset,
    mentionText,
    wikidataEntity,
    localEntityRef = null,
    signal,
} = {}) {
    if (!referenceId) throw new Error('referenceId is required');
    if (!userId) throw new Error('User ID is required');
    if (pmBlockIndex == null) throw new Error('pmBlockIndex is required');
    if (paragraphStartOffset == null || paragraphEndOffset == null) {
        throw new Error('paragraph offsets are required');
    }
    const rawId = String(
        localEntityRef || wikidataEntity?.id || wikidataEntity?.entityId || '',
    ).trim();
    if (!rawId) throw new Error('A Wikidata or custom entity id is required');

    const isLocal = isLocalEntityRef(rawId);

    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    const body = {
        pmBlockIndex: Number(pmBlockIndex),
        paragraphStartOffset: Number(paragraphStartOffset),
        paragraphEndOffset: Number(paragraphEndOffset),
        mentionText: String(mentionText ?? ''),
    };
    if (isLocal) {
        body.localEntityRef = rawId;
    } else {
        body.wikidataId = rawId;
    }
    if (wikidataEntity?.entityType) body.entityType = wikidataEntity.entityType;
    if (wikidataEntity?.label) body.label = wikidataEntity.label;
    if (wikidataEntity?.description) body.description = wikidataEntity.description;
    if (wikidataEntity?.imageUrl) body.imageUrl = wikidataEntity.imageUrl;

    const response = await fetch(
        `${baseUrl}/api/data-items/${encodeURIComponent(referenceId)}/manual-link?${query}`,
        {
            method: 'POST',
            headers: {
                ...getAwpClientHeaders(),
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-User-Id': userId,
            },
            body: JSON.stringify(body),
            signal,
        },
    );
    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to link entity (${response.status})`));
    }
    return response.json();
}

/**
 * Create a new highlight on a data item segment.
 *
 * Calls POST /api/highlights with { dataItemId, regions: [{segmentId, regionType, sortOrder}], comment?, owner? }
 * Returns the created HighlightResponse.
 */
export async function createFileHighlight({dataItemId, segmentId, regionType = 'FULL_SEGMENT', regionData = null, comment = null, owner = null, userId = null, signal} = {}) {
    if (!dataItemId) throw new Error('dataItemId is required');
    if (!segmentId) throw new Error('segmentId is required');
    const baseUrl = getSourcesApiBase();
    const region = {segmentId: Number(segmentId), regionType, sortOrder: 0};
    if (regionData && typeof regionData === 'object') region.regionData = regionData;
    const body = {
        dataItemId: Number(dataItemId),
        regions: [region],
    };
    if (comment) body.comment = comment;
    // owner scopes the highlight for per-user search recall; default to the viewer's id (same
    // convention as every other sourcesApi call) when the caller didn't set it explicitly.
    const resolvedOwner = owner || userId;
    if (resolvedOwner) body.owner = resolvedOwner;
    const response = await fetch(`${baseUrl}/api/highlights`, {
        method: 'POST',
        headers: {
            ...getAwpClientHeaders(),
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(userId ? {'X-User-Id': userId} : {}),
        },
        body: JSON.stringify(body),
        signal,
    });
    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to create highlight (${response.status})`));
    }
    return response.json();
}

/**
 * Fetch highlights for a My Data file.
 *
 * Calls GET /api/highlights?dataItemId={dataItemId}[&reportId={reportId}]
 * and returns the `content` array from the paginated response (all items, up to 100).
 *
 * Each highlight object shape (from HighlightResponse):
 *   { id, dataItemId, reportId, comment, owner, createdAt, regions: [{segmentId, regionType, sortOrder, regionData, numberInItem, preview}] }
 */
export async function fetchFileHighlights({dataItemId, reportId = null, signal} = {}) {
    if (!dataItemId) throw new Error('dataItemId is required');
    const baseUrl = getSourcesApiBase();
    const params = new URLSearchParams({dataItemId: String(dataItemId), size: '100', page: '0'});
    if (reportId) params.set('reportId', String(reportId));
    const response = await fetch(`${baseUrl}/api/highlights?${params.toString()}`, {
        method: 'GET',
        headers: {...getAwpClientHeaders(), Accept: 'application/json'},
        signal,
    });
    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to load highlights (${response.status})`));
    }
    const payload = await response.json();
    return Array.isArray(payload?.content) ? payload.content : (Array.isArray(payload) ? payload : []);
}

/**
 * Delete a single highlight by id.
 * Calls DELETE /api/highlights/{id}
 */
export async function deleteFileHighlight({highlightId, signal} = {}) {
    if (!highlightId) throw new Error('highlightId is required');
    const baseUrl = getSourcesApiBase();
    const response = await fetch(`${baseUrl}/api/highlights/${encodeURIComponent(highlightId)}`, {
        method: 'DELETE',
        headers: {...getAwpClientHeaders(), Accept: 'application/json'},
        signal,
    });
    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to delete highlight (${response.status})`));
    }
    return true;
}

/**
 * Federated unified search across the Corpus, My Data (data items), and approved Reports stores.
 *
 * @returns {Promise<{items: Array, meta: Object, searchId: (string|null)}>} ranked, interleaved
 *   hits — each carrying `documentStore` ('corpus' | 'data_item' | 'report'), `ownership`,
 *   `documentType`, `openTarget`, and (for data items) `sentenceHit` anchors for viewer highlight /
 *   seek-on-open. `searchId` (from the `X-Search-Id` response header) feeds the AI Summary endpoint.
 */
export async function searchUnified({
    q,
    userId,
    mode = 'hybrid',
    context,
    sources = 'both',
    scope = 'all',
    uploadedBranch,
    language = 'both',
    documentTypes,
    productTypes,
    tags,
    classificationLevels,
    docSource,
    limit = 20,
    includeDrafts = false,
    signal,
} = {}) {
    const query = String(q || '').trim();
    // Empty query is allowed when a tag filter is active (filter-browse mode) — the backend
    // returns items matched by tags alone. Without tags an empty query returns nothing.
    const hasTagFilter = Array.isArray(tags) && tags.some((t) => (typeof t === 'string' ? t : t?.name));
    if (!query && !hasTagFilter) {
        return {items: [], meta: {reason: 'empty_query'}, searchId: null};
    }

    const baseUrl = getSourcesApiBase();
    const params = new URLSearchParams();
    params.set('q', query);
    if (userId) params.set('userId', userId);
    if (mode) params.set('mode', mode);
    if (context && String(context).trim()) params.set('context', String(context).trim());
    if (sources) params.set('sources', sources);
    if (scope) params.set('scope', scope);
    if (uploadedBranch) params.set('uploadedBranch', uploadedBranch);
    if (language) params.set('language', language);
    if (limit) params.set('limit', String(limit));
    if (includeDrafts) params.set('includeDrafts', 'true');
    if (docSource) params.set('docSource', docSource);
    (documentTypes || []).forEach((t) => t && params.append('documentTypes', t));
    (productTypes || []).forEach((c) => c && params.append('productTypes', c));
    (tags || []).forEach((t) => {
        const name = typeof t === 'string' ? t : t?.name;
        if (name) params.append('tags', name);
    });
    (classificationLevels || []).forEach((c) => c && params.append('classificationLevels', c));

    const response = await fetch(`${baseUrl}/api/search/unified?${params.toString()}`, {
        method: 'GET',
        headers: {
            ...getAwpClientHeaders(),
            Accept: 'application/json',
            ...(userId ? {'X-User-Id': userId} : {}),
        },
        signal,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Search failed (${response.status})`));
    }

    const searchId = response.headers.get('X-Search-Id') || null;
    const payload = await response.json();
    return {
        items: Array.isArray(payload?.items) ? payload.items : [],
        meta: payload?.meta || {},
        searchId,
    };
}

